
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Icon } from '@/components/ui/Icon'
import { buildPowerPlan, type PowerDeviceFacts, type PowerPlanSnapshot } from '@/lib/power-plan'
import type { LoadedPowerPlan } from '@/lib/power-plan-server'
import { btn, cx, input, notice } from '@/lib/ui'
import { useUiMode } from '@/components/workspace/UiModeProvider'
import { PowerAnswer } from './PowerAnswer'
import { PowerResultBar } from './PowerResultBar'
import { PowerLadder } from './PowerLadder'
import { SupplyStrip } from './SupplyStrip'
import { PowerScheduleTable } from './PowerScheduleTable'
import { PowerGapsPanel } from './PowerGapsPanel'
import { PowerReconcilePanel } from './PowerReconcilePanel'
import { errorMessage } from '@/lib/client-api'

export type PowerDevicePick = PowerDeviceFacts & { sourceUrl: string | null }
export type PowerCanvas = { id: string; name: string; nodes: { id: string; deviceId: string; label: string }[] }

type Doc = { name: string; snapshot: PowerPlanSnapshot }

const SAVE_DEBOUNCE_MS = 800

/**
 * The editor shell.
 *
 * The plan is rebuilt in the browser on every edit and written back as a whole
 * document on a debounce — the same shape of save the canvas makes. Single
 * editor, no partial patches: a load schedule read half-applied is worse than
 * one saved a second late.
 */
export function PowerPlanClient({
  plan,
  devices,
  canvas,
  apiBase,
  basePath,
}: {
  plan: LoadedPowerPlan
  devices: PowerDevicePick[]
  canvas: PowerCanvas | null
  apiBase: string
  basePath: string
}) {
  // Name and snapshot move together as one document, so a save always writes a
  // consistent pair and the debounce has one thing to remember.
  const [doc, setDoc] = useState({ name: plan.name, snapshot: plan.snapshot })
  const [status, setStatus] = useState<'saved' | 'saving' | 'error'>('saved')
  const [error, setError] = useState<string | null>(null)
  /** Filters the table to one confidence bucket; set by clicking the result bar. */
  const [confidenceFilter, setConfidenceFilter] = useState<'known' | 'estimated' | 'unknown' | null>(null)
  // Was a local toggle; now the app-wide one, so a person who has asked for
  // detail once gets it everywhere instead of per page.
  const { advanced, setMode } = useUiMode()
  // A figure contributed from the Gaps panel is written server-side, so the
  // page has to come back for it rather than the plan being patched in place.
  const router = useRouter()

  const deviceMap = useMemo(
    () => new Map<string, PowerDeviceFacts>(devices.map((device) => [device.id, device])),
    [devices],
  )
  const deviceDetail = useMemo(
    () => new Map<string, PowerDevicePick>(devices.map((device) => [device.id, device])),
    [devices],
  )

  // The one call that turns the document into numbers. Everything on the page
  // reads off this, so nothing on screen can disagree with anything else.
  const built = useMemo(() => buildPowerPlan(doc.snapshot, deviceMap), [doc.snapshot, deviceMap])

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** What the next save will send. Only ever written from an event handler. */
  const pending = useRef<Doc | null>(null)

  const save = useCallback(async () => {
    const payload = pending.current
    if (!payload) return
    setStatus('saving')
    setError(null)
    try {
      const response = await fetch(`${apiBase}/power/${plan.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: payload.name,
          projectId: plan.projectId,
          notes: plan.notes,
          snapshot: payload.snapshot,
        }),
      })
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { error?: string } | null
        throw new Error(body?.error ?? 'Could not save')
      }
      // Anything typed while the request was in flight is still pending, so the
      // document is only clean when nothing newer arrived behind it.
      if (pending.current === payload) {
        pending.current = null
        setStatus('saved')
      }
    } catch (cause) {
      setStatus('error')
      setError(errorMessage(cause, 'Could not save'))
    }
  }, [apiBase, plan.id, plan.projectId, plan.notes])

  const commit = useCallback(
    (next: Doc) => {
      setDoc(next)
      pending.current = next
      setStatus('saving')
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => void save(), SAVE_DEBOUNCE_MS)
    },
    [save],
  )

  // Closing the tab mid-debounce would drop the last edit silently.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (pending.current) event.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => {
      window.removeEventListener('beforeunload', warn)
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const edit = useCallback(
    (next: (current: PowerPlanSnapshot) => PowerPlanSnapshot) =>
      commit({ name: doc.name, snapshot: next(doc.snapshot) }),
    [commit, doc],
  )

  return (
    <div className="mx-auto w-full max-w-6xl px-5 py-8 sm:px-8 sm:py-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link
            href={`${basePath}/power`}
            className="inline-flex items-center gap-1 text-[11px] text-muted transition-colors hover:text-foreground"
          >
            <Icon name="back" className="h-3 w-3" />
            Power
          </Link>
          <input
            aria-label="Plan name"
            value={doc.name}
            onChange={(event) => commit({ name: event.target.value, snapshot: doc.snapshot })}
            className={cx(
              input,
              'mt-1 border-transparent bg-transparent px-0 text-2xl font-semibold tracking-tight shadow-none focus:border-transparent focus:bg-transparent focus:ring-0',
            )}
          />
          {plan.projectName && (
            <p className="text-[11px] text-muted">
              from{' '}
              <Link href={`${basePath}/projects/${plan.projectId}`} className="underline underline-offset-2">
                {plan.projectName}
              </Link>
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span
            aria-live="polite"
            className={cx('text-[11px]', status === 'error' ? 'text-danger' : 'text-muted')}
          >
            {status === 'saving' ? 'Saving…' : status === 'error' ? 'Not saved' : 'Saved'}
          </span>
          <a href={`${apiBase}/power/${plan.id}/power-plan.csv`} className={cx(btn.sm, btn.secondary)}>
            <Icon name="sheet" className="h-3.5 w-3.5" />
            CSV
          </a>
          <a href={`${apiBase}/power/${plan.id}/power-plan.pdf`} className={cx(btn.sm, btn.secondary)}>
            <Icon name="download" className="h-3.5 w-3.5" />
            PDF
          </a>
        </div>
      </div>

      {error && (
        <p role="alert" className={cx(notice.error, 'mt-4')}>
          {error}
        </p>
      )}

      {plan.drift.length > 0 && (
        <p className={cx(notice.info, 'mt-4')}>
          {plan.drift.length} {plan.drift.length === 1 ? 'row differs' : 'rows differ'} from the
          catalogue since this plan was saved. The plan keeps its own figures — a dated document does
          not change under you.{' '}
          <span className="text-muted">
            {plan.drift
              .slice(0, 3)
              .map((row) => `${row.label} ${row.was ?? '—'} → ${row.now ?? '—'} W`)
              .join(' · ')}
          </span>
        </p>
      )}

      <div className="mt-5 space-y-4">
        {/* The answer, then the gear that produced it. Nothing between them. */}
        <PowerAnswer built={built} />

        <PowerScheduleTable
          snapshot={doc.snapshot}
          built={built}
          devices={devices}
          deviceDetail={deviceDetail}
          confidenceFilter={confidenceFilter}
          showCircuits={advanced}
          onChange={edit}
        />

        {/* Everything below is an electrician's view of the same numbers. It is
            collapsed because the two people who need it are not the person the
            page is for, and an unopened panel is cheaper than a wrong default. */}
        <div>
          <button
            type="button"
            onClick={() => setMode(advanced ? 'SIMPLE' : 'ADVANCED')}
            aria-expanded={advanced}
            className="inline-flex items-center gap-1 text-[11px] text-muted transition-colors hover:text-foreground"
          >
            <Icon name={advanced ? 'chevronDown' : 'chevronRight'} className="h-3 w-3" />
            {advanced ? 'Hide' : 'Show'} circuits, supply and phase detail
            <span className="ml-1 opacity-70">· switches the whole app</span>
          </button>
        </div>

        {advanced && (
          <>
            <PowerResultBar
              built={built}
              settings={doc.snapshot.settings}
              confidenceFilter={confidenceFilter}
              onConfidenceFilter={setConfidenceFilter}
            />
            <PowerLadder built={built} settings={doc.snapshot.settings} />
            <PowerGapsPanel
              built={built}
              deviceDetail={deviceDetail}
              snapshot={doc.snapshot}
              apiBase={apiBase}
              onFigureSaved={() => router.refresh()}
            />
            <SupplyStrip
              settings={doc.snapshot.settings}
              onChange={(settings) => edit((current) => ({ ...current, settings }))}
            />
            {canvas && (
              <PowerReconcilePanel
                canvas={canvas}
                snapshot={doc.snapshot}
                built={built}
                devices={deviceDetail}
                basePath={basePath}
                onChange={edit}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
